<template>
  <VanPopup
    :show="show"
    @update:show="emit('update:show', $event)"
    position="bottom"
  >
    <ThreadsNav
      show-back
      :go-back="() => emit('update:show', false)"
      :show-center="false"
    >
      <template #right>
        <button class="btn btn-primary btn-xs rounded" @click="onConfirm">
          {{ $t("Buttons.confirm") }}
        </button>
      </template>
    </ThreadsNav>

    <div class="h-[80vh] flex flex-col">
      <!-- 顶部选择状态栏 -->
      <div
        v-if="mateList.length > 0"
        class="p-4 border-b border-base-200 bg-base-100"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <button
              class="btn btn-sm"
              :class="allSelected ? 'btn-primary' : 'btn-outline'"
              @click="toggleSelectAll"
            >
              全选
            </button>
            <span class="text-sm text-base-content/60">
              已选择 {{ selectedMateList.length }} / {{ mateList.length }}
            </span>
          </div>

          <button
            v-if="selectedMateList.length > 0"
            class="btn btn-sm btn-outline"
            @click="clearSelection"
          >
            清空
          </button>
        </div>
      </div>

      <!-- 列表内容 -->
      <div class="flex-1 overflow-y-auto p-4 space-y-3">
        <div
          v-for="mate in mateList"
          :key="mate.id || mate.uuid"
          class="flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 cursor-pointer"
          :class="{
            'border-primary bg-primary/10': isSelected(mate),
            'border-base-300 bg-base-100 hover:bg-base-200': !isSelected(mate),
          }"
          @click="toggleMateSelection(mate)"
        >
          <!-- 选择框 -->
          <div class="flex-shrink-0">
            <input
              type="checkbox"
              :checked="isSelected(mate)"
              class="checkbox checkbox-primary"
              readonly
            />
          </div>

          <!-- 头像 -->
          <div
            class="flex-shrink-0 w-12 h-16 rounded-lg overflow-hidden bg-base-300"
          >
            <BaseImg
              v-if="mate.mediaUrl"
              :url="mate.mediaUrl"
              ratio="3:4"
              fit="cover"
              class="w-full h-full"
            />
            <div
              v-else
              class="w-full h-full flex items-center justify-center text-base-content/40"
            >
              <i class="text-2xl">👤</i>
            </div>
          </div>

          <!-- 信息 -->
          <div class="flex-1 min-w-0">
            <div class="font-medium text-base truncate">
              {{ mate.name || "未命名" }}
            </div>
            <div v-if="mate.tag?.length" class="flex gap-1 mt-2">
              <span
                v-for="tag in mate.tag.slice(0, 3)"
                :key="tag"
                class="badge badge-sm badge-outline"
              >
                {{ tag }}
              </span>
              <span
                v-if="mate.tag.length > 3"
                class="text-xs text-base-content/40"
              >
                +{{ mate.tag.length - 3 }}
              </span>
            </div>
          </div>

          <!-- 右侧状态 -->
          <div class="flex-shrink-0">
            <div v-if="isSelected(mate)" class="text-primary text-xl">✓</div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="mateList.length === 0" class="text-center py-16">
          <div class="text-6xl mb-4">🤖</div>
          <div class="text-lg font-medium mb-2">暂无角色</div>
          <div class="text-sm text-base-content/60">请先创建一些角色</div>
        </div>
      </div>
    </div>
  </VanPopup>
</template>

<script lang="ts" setup>
import { useMate } from "@/stores/mate";
import { Mate } from "@/types/entity";
import { computed, ref, watch } from "vue";

const props = defineProps<{
  show: boolean;
  onlyHasAvatar?: boolean;
  max?: number;
}>();

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "confirm", mateList: Mate[]): void;
}>();

const mateStore = useMate();
const mateList = computed(() =>
  props.onlyHasAvatar
    ? mateStore.fullListWithSelfAvatar.filter((mate) => mate.mediaUrl)
    : mateStore.fullListWithSelfAvatar
);
const selectedMateList = ref<Mate[]>([]);

watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      selectedMateList.value = [];
    }
  }
);

// 计算属性
const allSelected = computed(
  () =>
    mateList.value.length > 0 &&
    selectedMateList.value.length === mateList.value.length
);

// 方法
const isSelected = (mate: Mate): boolean => {
  return selectedMateList.value.some(
    (selected) =>
      (selected.id && selected.id === mate.id) ||
      (selected.uuid && selected.uuid === mate.uuid)
  );
};

const toggleMateSelection = (mate: Mate) => {
  const isCurrentlySelected = isSelected(mate);

  if (isCurrentlySelected) {
    // 取消选择
    selectedMateList.value = selectedMateList.value.filter(
      (selected) =>
        !(
          (selected.id && selected.id === mate.id) ||
          (selected.uuid && selected.uuid === mate.uuid)
        )
    );
  } else {
    // 添加选择
    selectedMateList.value.push(mate);
  }
};

const toggleSelectAll = () => {
  if (allSelected.value) {
    selectedMateList.value = [];
  } else {
    selectedMateList.value = [...mateList.value];
  }
};

const clearSelection = () => {
  selectedMateList.value = [];
};

const onConfirm = () => {
  emit("confirm", selectedMateList.value);
  emit("update:show", false);
};
</script>

<style scoped>
/* 支持 indeterminate 状态的复选框样式 */
input[type="checkbox"]:indeterminate {
  background-color: hsl(var(--p));
  border-color: hsl(var(--p));
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M4 8h8'/%3e%3c/svg%3e");
}
</style>
